/**
 * Authentication context for ClarCV application.
 *
 * Manages login, registration, logout, JWT refresh, and session restore
 * (including after returning from external redirects like Stripe Checkout).
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buildSignupAttributionPayload } from '../utils/attribution';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  readStoredAccessToken,
  syncAxiosAuth,
} from '../utils/authStorage';
import { CONSENT_ACCEPTED, hasAnalyticsConsent, getCookieConsent } from '../utils/cookieConsent';

const AuthContext = createContext();

const initialAccess = readStoredAccessToken();
if (initialAccess) {
  syncAxiosAuth(axios, initialAccess);
}

/**
 * Custom hook to access authentication context.
 *
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Persist tokens and set axios Authorization header.
 *
 * @param {string} access - JWT access token
 * @param {string} [refresh] - JWT refresh token
 */
const applyTokens = (access, refresh) => {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh);
  }
  syncAxiosAuth(axios, access);
};

/**
 * Clear tokens and Authorization header.
 */
const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  syncAxiosAuth(axios, '');
};

const signupAttributionBody = () =>
  buildSignupAttributionPayload({
    analyticsConsent: hasAnalyticsConsent(),
    adConsent: getCookieConsent() === CONSENT_ACCEPTED,
  });

/**
 * Authentication provider component.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Authentication provider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => initialAccess || null);
  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    axios.defaults.baseURL = baseURL;
  }, []);

  useEffect(() => {
    syncAxiosAuth(axios, token || '');
  }, [token]);

  /**
   * Refresh access token using stored refresh token.
   * Deduplicates concurrent refresh attempts.
   *
   * @returns {Promise<string>} New access token
   */
  const refreshAccessToken = async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      const refresh = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (!refresh) {
        throw new Error('No refresh token');
      }
      const { data } = await axios.post('/api/auth/jwt/refresh/', { refresh });
      const access = data.access;
      const nextRefresh = data.refresh || refresh;
      applyTokens(access, nextRefresh);
      setToken(access);
      return access;
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  };

  /**
   * Load subscription plan for the current user.
   *
   * @returns {Promise<Object>} Subscription payload
   */
  const fetchSubscription = async () => {
    try {
      const { data } = await axios.get('/api/cvs/subscription/');
      setSubscription(data);
      return data;
    } catch (error) {
      console.error('Error loading subscription:', error);
      // A 401 right after Stripe is often a missing header, not a Free plan.
      // Do not overwrite a real subscription with a Free fallback.
      if (error.response?.status === 401) {
        return null;
      }
      const fallback = { plan: 'free', is_active: true };
      setSubscription(fallback);
      return fallback;
    }
  };

  /**
   * Load current user profile; refresh JWT once on 401 before giving up.
   *
   * @returns {Promise<Object|null>} User profile or null
   */
  const fetchProfile = async () => {
    const access = readStoredAccessToken();
    if (!access) {
      return null;
    }
    syncAxiosAuth(axios, access);
    try {
      const response = await axios.get('/api/users/profile/');
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        try {
          await refreshAccessToken();
          const response = await axios.get('/api/users/profile/');
          return response.data;
        } catch (refreshError) {
          console.error('Session expired; could not refresh token:', refreshError);
          return null;
        }
      }
      // Network / 5xx: keep tokens so a full reload after Stripe does not wipe the session
      console.error('Error verifying authentication:', error);
      throw error;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const profile = await fetchProfile();
        if (profile) {
          setUser(profile);
          await fetchSubscription();
        } else {
          clearTokens();
          setToken(null);
          setUser(null);
          setSubscription(null);
        }
      } catch (error) {
        // Keep existing token; user may still be valid after transient errors
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when access token identity changes
  }, [token]);

  // Retry once on 401 for API calls after returning from external redirects
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (!original || original._retry || error.response?.status !== 401) {
          return Promise.reject(error);
        }
        if (original.url?.includes('/api/auth/jwt/') || original.url?.includes('/api/auth/google/')) {
          return Promise.reject(error);
        }
        original._retry = true;
        try {
          const access = await refreshAccessToken();
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${access}`;
          return axios(original);
        } catch (refreshError) {
          clearTokens();
          setToken(null);
          setUser(null);
          setSubscription(null);
          return Promise.reject(refreshError);
        }
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Login user with email and password.
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result with success status and error message
   */
  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/jwt/create/', {
        email,
        password,
      });

      const { access, refresh } = response.data;
      applyTokens(access, refresh);
      setToken(access);

      const userResponse = await axios.get('/api/users/profile/');
      setUser(userResponse.data);
      await fetchSubscription();

      return { success: true };
    } catch (error) {
      console.error('Error in login:', error);
      let errorMessage = 'Error logging in';

      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = Array.isArray(error.response.data.non_field_errors)
            ? error.response.data.non_field_errors.join(', ')
            : error.response.data.non_field_errors;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  /**
   * Login or register with a Google Identity Services ID token.
   *
   * @param {string} credential - Google JWT ID token
   * @returns {Promise<Object>} Result with success, created, and error
   */
  const loginWithGoogle = async (credential) => {
    try {
      const response = await axios.post(
        '/api/auth/google/',
        { credential, attribution: signupAttributionBody() },
        {
          headers: {
            Authorization: undefined,
          },
        }
      );
      const { access, refresh, created, user: profile } = response.data;
      applyTokens(access, refresh);
      setToken(access);

      if (profile) {
        setUser(profile);
      } else {
        const userResponse = await axios.get('/api/users/profile/');
        setUser(userResponse.data);
      }
      await fetchSubscription();

      return { success: true, created: Boolean(created) };
    } catch (error) {
      console.error('Error in Google login:', error);
      let errorMessage = 'Error logging in with Google';
      const data = error.response?.data;
      if (data?.error) {
        errorMessage = data.error;
      } else if (data?.detail) {
        errorMessage = data.detail;
      } else if (typeof data === 'string') {
        errorMessage = data;
      }
      return {
        success: false,
        error: errorMessage,
        errorCode: data?.error_code || '',
      };
    }
  };

  /**
   * Register new user.
   *
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration result with success status and error message
   */
  const register = async (userData) => {
    try {
      const payload = {
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        password: userData.password,
        re_password: userData.re_password || userData.password,
        attribution: signupAttributionBody(),
      };
      if (userData.turnstile_token) {
        payload.turnstile_token = userData.turnstile_token;
      }
      const response = await axios.post('/api/auth/users/', payload, {
        headers: {
          'Accept-Language':
            axios.defaults.headers.common['Accept-Language'] || 'es',
          'X-ClarCV-Lang':
            axios.defaults.headers.common['X-ClarCV-Lang'] || 'es',
        },
      });
      return { success: true, data: response.data, needsActivation: true };
    } catch (error) {
      console.error('Error in registration:', error);
      let errorMessage = 'Error registering';

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.turnstile_token) {
          errorMessage = Array.isArray(data.turnstile_token)
            ? data.turnstile_token.join(', ')
            : data.turnstile_token;
        } else if (data.password) {
          errorMessage = Array.isArray(data.password)
            ? data.password.join(', ')
            : data.password;
        } else if (data.email) {
          errorMessage = Array.isArray(data.email)
            ? data.email.join(', ')
            : data.email;
        } else if (data.username) {
          errorMessage = Array.isArray(data.username)
            ? data.username.join(', ')
            : data.username;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors)
            ? data.non_field_errors.join(', ')
            : data.non_field_errors;
        } else {
          errorMessage = JSON.stringify(data);
        }
      }

      return {
        success: false,
        error: errorMessage,
        errorCode: error.response?.data?.error_code || '',
      };
    }
  };

  /**
   * Logout user and clear authentication data.
   */
  const logout = () => {
    clearTokens();
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  /**
   * Permanently delete the current account and clear the local session.
   *
   * @param {{ confirmation: string, password?: string }} payload
   * @returns {Promise<{ success: boolean, error?: string, errorCode?: string }>}
   */
  const deleteAccount = async ({ confirmation, password = '' }) => {
    try {
      await axios.post('/api/users/delete-account/', {
        confirmation,
        password,
      });
      logout();
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.error || 'Could not delete the account.',
        errorCode: data.error_code || '',
      };
    }
  };

  const plan = (subscription?.plan || 'free').toLowerCase();
  const isPro = Boolean(
    subscription?.is_active && (plan === 'pro' || plan === 'premium')
  );
  const cancelAtPeriodEnd = Boolean(subscription?.cancel_at_period_end);
  const currentPeriodEnd = subscription?.current_period_end || null;

  const value = {
    user,
    subscription,
    plan,
    isPro,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    refreshSubscription: fetchSubscription,
    login,
    loginWithGoogle,
    register,
    logout,
    deleteAccount,
    loading,
    // Keep UI logged-in while restoring session after Stripe redirect
    isAuthenticated: !!user || (!!token && loading),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
